import React, { useEffect, createElement } from 'react';
interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  noindex?: boolean;
}
export function SEOHead({
  title,
  description,
  canonical,
  ogImage = 'https://cdn.magicpatterns.com/uploads/5m425UHDJynsrvkfvBo6sp/sj-website-logo-colour.svg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noindex = false
}: SEOHeadProps) {
  useEffect(() => {
    // Set document title
    document.title = title;
    // Helper to set or create a meta tag
    const setMeta = (attr: string, attrValue: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${attrValue}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    // Helper to set or create a link tag
    const setLink = (rel: string, href: string, crossorigin?: boolean) => {
      let el = document.querySelector(`link[rel="${rel}"][href="${href}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('href', href);
        if (crossorigin) {
          el.setAttribute('crossorigin', '');
        }
        document.head.appendChild(el);
      }
    };
    // Preconnect to CDN for faster image/asset loading
    setLink('preconnect', 'https://cdn.magicpatterns.com', true);
    setLink('dns-prefetch', 'https://cdn.magicpatterns.com');
    // Preconnect to Google Fonts for faster font loading
    setLink('preconnect', 'https://fonts.googleapis.com');
    setLink('preconnect', 'https://fonts.gstatic.com', true);
    // Load Google Fonts asynchronously (non-render-blocking)
    const googleFontsHref = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Playfair+Display:ital,wght@1,400;1,700&family=Poppins:wght@400;500;600&display=swap';
    if (!document.querySelector(`link[href="${googleFontsHref}"]`)) {
      // Preload the font CSS
      const preloadLink = document.createElement('link');
      preloadLink.setAttribute('rel', 'preload');
      preloadLink.setAttribute('as', 'style');
      preloadLink.setAttribute('href', googleFontsHref);
      document.head.appendChild(preloadLink);
      // Load font CSS asynchronously using media trick
      const fontLink = document.createElement('link');
      fontLink.setAttribute('rel', 'stylesheet');
      fontLink.setAttribute('href', googleFontsHref);
      fontLink.setAttribute('media', 'print');
      fontLink.onload = function () {
        fontLink.media = 'all';
      };
      document.head.appendChild(fontLink);
      // Fallback noscript (for non-JS environments)
      const noscript = document.createElement('noscript');
      const noscriptLink = document.createElement('link');
      noscriptLink.setAttribute('rel', 'stylesheet');
      noscriptLink.setAttribute('href', googleFontsHref);
      noscript.appendChild(noscriptLink);
      document.head.appendChild(noscript);
    }
    // Preload LCP hero image for faster Largest Contentful Paint
    const heroImageHref = 'https://cdn.magicpatterns.com/uploads/fssYpKf8swLzdNNaRcNwxG/sj-website-home-banner-1.png';
    if (!document.querySelector(`link[rel="preload"][href="${heroImageHref}"]`)) {
      const preloadImg = document.createElement('link');
      preloadImg.setAttribute('rel', 'preload');
      preloadImg.setAttribute('as', 'image');
      preloadImg.setAttribute('href', heroImageHref);
      preloadImg.setAttribute('fetchpriority', 'high');
      document.head.appendChild(preloadImg);
    }
    // Meta description
    setMeta('name', 'description', description);
    // Robots
    if (noindex) {
      setMeta('name', 'robots', 'noindex, nofollow');
    } else {
      setMeta('name', 'robots', 'index, follow');
    }
    // Canonical
    if (canonical) {
      const canonEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonEl) {
        const el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        el.setAttribute('href', canonical);
        document.head.appendChild(el);
      } else {
        canonEl.setAttribute('href', canonical);
      }
    }
    // Open Graph
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:site_name', 'Smith & Jengs Cleaning Services');
    if (ogImage) {
      setMeta('property', 'og:image', ogImage);
    }
    if (canonical) {
      setMeta('property', 'og:url', canonical);
    }
    // Twitter Card
    setMeta('name', 'twitter:card', twitterCard);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    if (ogImage) {
      setMeta('name', 'twitter:image', ogImage);
    }
    // Cleanup: reset title on unmount
    return () => {
      document.title = 'Smith & Jengs Cleaning Services';
    };
  }, [title, description, canonical, ogImage, ogType, twitterCard, noindex]);
  return null;
}